import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedFetch } from '../utils';
import { useSettingsQuery } from '../hooks/useSettingsQuery';
import { useAuth } from './AuthContext';
import { browserTimeZone, isValidTimeZone } from '../lib/time';
import { DEFAULT_TARGETS, TARGET_PRESETS } from '../lib/metrics';

/**
 * Business preferences — timezone, targets, SLA, and business hours.
 *
 * These live in tenant_settings.other_settings, a JSONB column that already
 * exists and is already read by the backend, so none of this needs a migration.
 *
 * TIMEZONE IS THE IMPORTANT ONE. Every analytics request sends `tz` so the
 * backend can bucket by the business's own calendar day instead of UTC. Until a
 * tenant picks one we fall back to the browser's zone, which is a far better
 * guess than UTC and is surfaced in Settings as "detected, not confirmed".
 */

const PreferencesContext = createContext(null);

export const usePreferences = () => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
};

const DEFAULT_BUSINESS_HOURS = { start: 9, end: 18, days: [0, 1, 2, 3, 4] }; // Mon–Fri, 0 = Monday

export const PreferencesProvider = ({ children }) => {
  const queryClient = useQueryClient();
  // Gate on auth here rather than via a prop: the provider sits above the routes,
  // so a caller has no good place to pass it, and an unauthenticated settings
  // fetch is a guaranteed 401 on every page load of the login screen.
  const { authenticated } = useAuth();
  const { data: settings, isLoading, isError } = useSettingsQuery(Boolean(authenticated));

  const other = settings?.otherSettings || {};

  // A configured zone wins; an invalid stored value must not break the app.
  const configuredTz = isValidTimeZone(other.timezone) ? other.timezone : null;
  const detectedTz = browserTimeZone();
  const timeZone = configuredTz || detectedTz;

  const targets = useMemo(
    () => ({ ...DEFAULT_TARGETS, ...(other.metric_targets || {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(other.metric_targets || {})]
  );

  const businessHours = useMemo(
    () => ({ ...DEFAULT_BUSINESS_HOURS, ...(other.business_hours || {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(other.business_hours || {})]
  );

  const hour12 = other.hour12 !== false;

  /** Which targets the tenant actually chose, so defaults can be shown as defaults. */
  const targetSource = useMemo(() => {
    const explicit = other.metric_targets || {};
    return Object.keys(DEFAULT_TARGETS).reduce((acc, k) => {
      acc[k] = k in explicit ? 'tenant' : 'default';
      return acc;
    }, {});
  }, [other.metric_targets]);

  const mutation = useMutation({
    mutationFn: async (patch) => {
      const response = await authenticatedFetch('/api/tenant/settings', {
        method: 'POST',
        body: JSON.stringify({ otherSettings: { ...other, ...patch } }),
      });
      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(detail || 'Could not save preferences');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      // Every analytics key is scoped by timezone, so they all have to go.
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });

  const setTimeZone = useCallback((tz) => mutation.mutateAsync({ timezone: tz }), [mutation]);

  const setTargets = useCallback(
    (patch) => mutation.mutateAsync({ metric_targets: { ...(other.metric_targets || {}), ...patch } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutation, JSON.stringify(other.metric_targets || {})]
  );

  const setBusinessHours = useCallback(
    (patch) => mutation.mutateAsync({ business_hours: { ...businessHours, ...patch } }),
    [mutation, businessHours]
  );

  const setHour12 = useCallback((v) => mutation.mutateAsync({ hour12: !!v }), [mutation]);

  const applyTargetPreset = useCallback(
    (presetName) => {
      const preset = TARGET_PRESETS[presetName] || TARGET_PRESETS.default;
      return mutation.mutateAsync({ metric_targets: preset });
    },
    [mutation]
  );

  const value = useMemo(
    () => ({
      timeZone,
      configuredTz,
      detectedTz,
      isTimeZoneConfirmed: Boolean(configuredTz),
      targets,
      targetSource,
      businessHours,
      hour12,
      preferencesLoading: isLoading,
      preferencesError: isError,
      saving: mutation.isPending,
      saveError: mutation.error,
      setTimeZone,
      setTargets,
      setBusinessHours,
      setHour12,
      applyTargetPreset,
    }),
    [
      timeZone, configuredTz, detectedTz, targets, targetSource, businessHours, hour12,
      isLoading, isError, mutation.isPending, mutation.error,
      setTimeZone, setTargets, setBusinessHours, setHour12, applyTargetPreset,
    ]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};
